import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Stack,
  alpha,
  useTheme,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Close,
  Person,
  Receipt,
  History,
  Add,
  Phone,
  WhatsApp,
  Email,
  RecordVoiceOver,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Handshake,
} from '@mui/icons-material';
import { formatarData, formatarValor } from '../../../utils/formatToView';
import { Client, Invoice, CobrancaFinanceiro, CobrancaHistorico } from '../../../types';
import RenegociarCobrancaModal from './RenegociarCobrancaModal';
import { cobrancaHistoricoService } from '../../../services/cobrancaHistoricoService';
import { toast } from 'react-toastify';
import { useAuth } from '../../../contexts/AuthContext';
import { formatarPhone } from '../../../utils/formatToView';
import { CobrancaFinanceiroService } from '../../../services/CobrancaFinanceiroService';

interface GerenciarCobrancaModalProps {
  open: boolean;
  onClose: () => void;
  cobrancaId: number | null;
  onSuccess?: () => void;
}

const TIPO_CONTATO_OPTIONS = [
  { value: 'telefone', label: 'Telefone', icon: <Phone sx={{ fontSize: 16 }} /> },
  { value: 'whatsapp', label: 'WhatsApp', icon: <WhatsApp sx={{ fontSize: 16 }} /> },
  { value: 'email', label: 'Email', icon: <Email sx={{ fontSize: 16 }} /> },
  { value: 'presencial', label: 'Presencial', icon: <Person sx={{ fontSize: 16 }} /> },
  { value: 'sms', label: 'SMS', icon: <Phone sx={{ fontSize: 16 }} /> },
  { value: 'sistema', label: 'Sistema', icon: <History sx={{ fontSize: 16 }} /> },
  { value: 'outro', label: 'Outro', icon: <Add sx={{ fontSize: 16 }} /> },
];


const STATUS_COBRANCA_OPTIONS = [
  { value: 'pendente', label: 'Pendente', color: 'default' },
  { value: 'em_contato', label: 'Em Contato', color: 'info' },
  { value: 'promessa_pagamento', label: 'Promessa Pgto', color: 'warning' },
  { value: 'pagou', label: 'Pagou', color: 'success' },
  { value: 'sem_retorno', label: 'Sem Retorno', color: 'error' },
  { value: 'negociacao', label: 'Em Negociação', color: 'secondary' },
  { value: 'cancelado', label: 'Cancelado', color: 'error' },
  { value: 'finalizado', label: 'Finalizado', color: 'success' },
];

/**
 * Calcula a diferença de dias entre a data de vencimento e hoje.
 */
const getDaysOverdue = (dueDate: string | Date | undefined) => {
  if (!dueDate) return 0;
  const today = new Date();
  const due = new Date(dueDate);
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  const diffTime = today.getTime() - due.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
};

const GerenciarCobrancaModal: React.FC<GerenciarCobrancaModalProps> = ({ open, onClose, cobrancaId, onSuccess }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [historico, setHistorico] = useState<CobrancaHistorico[]>([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [addingMovement, setAddingMovement] = useState(false);
  const [renegotiateOpen, setRenegotiateOpen] = useState(false);
  const [currentCobranca, setCurrentCobranca] = useState<CobrancaFinanceiro | null>(null);

  useEffect(() => {
    if (open && cobrancaId) {
      fetchCobrancaAtualizada(cobrancaId);
      setAddingMovement(false);
      resetForm();
    } else if (!open) {
      setCurrentCobranca(null);
      setHistorico([]);
    }
  }, [open, cobrancaId]);

  // Form states for new movement
  const [tipoContato, setTipoContato] = useState('whatsapp');
  const [falouCom, setFalouCom] = useState('');
  const [descricao, setDescricao] = useState('');
  const [valorProposta, setValorProposta] = useState('');
  const [dataPromessa, setDataPromessa] = useState('');
  const [statusPosContato, setStatusPosContato] = useState('em_contato');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && currentCobranca?.id) {
      fetchHistorico();
      setAddingMovement(false);
      resetForm();
    }
  }, [open, currentCobranca?.id]);

  const fetchCobrancaAtualizada = async (id?: number) => {
    const targetId = id || currentCobranca?.id;
    if (!targetId) return;
    
    setLoadingData(true);
    try {
      const resp = await CobrancaFinanceiroService.getById(targetId);
      setCurrentCobranca(resp);
      fetchHistorico(targetId);
    } catch (err) {
      console.error('Erro ao atualizar cobrança:', err);
      toast.error('Erro ao carregar dados da cobrança');
    } finally {
      setLoadingData(false);
    }
  };

  const fetchHistorico = async (id?: number) => {
    const targetId = id || currentCobranca?.id;
    if (!targetId) return;

    setLoadingHistorico(true);
    try {
      const response = await cobrancaHistoricoService.list(targetId);
      // O interceptor do axios retorna response.data, mas dependendo de como o service foi tipado,
      // a resposta pode ser o array direto ou { success, data }.
      const data = Array.isArray(response) ? response : (response as any).data || [];
      setHistorico(data);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoadingHistorico(false);
    }
  };

  const resetForm = (status?: string) => {
    setTipoContato('whatsapp');
    setFalouCom('');
    setDescricao('');
    setValorProposta('');
    setDataPromessa('');
    setStatusPosContato(status || currentCobranca?.status || 'em_contato');
  };

  const handleCreateMovement = async () => {
    if (!currentCobranca?.id) return;
    if (!descricao) {
      toast.warning('Informe a descrição do contato');
      return;
    }

    setSubmitting(true);
    try {
      await cobrancaHistoricoService.create({
        cobranca_id: currentCobranca.id,
        tipo_contato: tipoContato as any,
        falou_com: falouCom,
        descricao,
        valor_proposta: valorProposta ? Number(valorProposta) : null,
        data_promessa: dataPromessa || null,
        status_pos_contato: statusPosContato as any,
      });

      toast.success('Movimentação adicionada!');
      setAddingMovement(false);
      fetchHistorico();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Erro ao criar movimentação:', error);
      toast.error('Erro ao criar movimentação');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingData && !currentCobranca) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  if (!currentCobranca) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <History color="primary" />
          <Typography variant="h6" fontWeight={800}>Gerenciar Cobrança #{currentCobranca.id}</Typography>
          <Chip
            label={STATUS_COBRANCA_OPTIONS.find(o => o.value === currentCobranca.status)?.label}
            color={STATUS_COBRANCA_OPTIONS.find(o => o.value === currentCobranca.status)?.color as any}
            sx={{ fontWeight: 800 }}
          />
        </Stack>
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 1 }}>
        <Grid container spacing={1}>
          {/* Dados do Cliente */}
          {/* Dados Detalhados do Cliente */}
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 1, border: `0px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person color="primary" /> Perfil do Cliente
                </Typography>
              </Stack>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Nome / Razão Social</Typography>
                  <Typography
                    variant="body2"
                    fontWeight={800}
                    sx={{
                      cursor: currentCobranca.cliente_id ? 'pointer' : 'default',
                      '&:hover': currentCobranca.cliente_id ? {
                        color: 'primary.main',
                        textDecoration: 'underline',
                      } : {},
                      transition: 'color 0.2s',
                    }}
                    onClick={() => currentCobranca.cliente_id && navigate(`/clients/${currentCobranca.cliente_id}`)}
                  >
                    {currentCobranca.cliente?.nome}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>CPF / CNPJ</Typography>
                  <Typography variant="body2">{currentCobranca.cliente?.cpf_cnpj}</Typography>
                </Grid>
                {currentCobranca.cliente?.rg_ie && (
                  <Grid item xs={12} sm={3}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>RG / IE</Typography>
                    <Typography variant="body2">{currentCobranca.cliente.rg_ie}</Typography>
                  </Grid>
                )}

                {currentCobranca.cliente?.data_nascimento && (
                  <Grid item xs={12} sm={3}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Nascimento</Typography>
                    <Typography variant="body2">{formatarData(currentCobranca.cliente.data_nascimento)}</Typography>
                  </Grid>
                )}
                {currentCobranca.cliente?.nome_mae && (
                  <Grid item xs={12} sm={3}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Mãe</Typography>
                    <Typography variant="body2">{currentCobranca.cliente.nome_mae}</Typography>
                  </Grid>
                )}
                {currentCobranca.cliente?.nome_pai && (
                  <Grid item xs={12} sm={3}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Pai</Typography>
                    <Typography variant="body2">{currentCobranca.cliente.nome_pai}</Typography>
                  </Grid>
                )}
                {currentCobranca.cliente?.tipo_moradia && (
                  <Grid item xs={12} sm={3}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Moradia</Typography>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{currentCobranca.cliente.tipo_moradia}</Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Divider sx={{ my: 0, borderStyle: 'dashed' }} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Endereço</Typography>
                      <Typography variant="body2" fontWeight={800}>
                        {currentCobranca.cliente?.endereco}{currentCobranca.cliente?.numero ? `, ${currentCobranca.cliente.numero}` : ''}
                        {currentCobranca.cliente?.complemento ? ` - ${currentCobranca.cliente.complemento}` : ''}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {currentCobranca.cliente?.bairro} - {currentCobranca.cliente?.cidade} / {currentCobranca.cliente?.estado}
                      </Typography>
                    </Box>

                    {currentCobranca.cliente?.observacoes && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Observações</Typography>
                        <Box sx={{ p: 1, bgcolor: alpha(theme.palette.warning.main, 0.05), borderRadius: 1, border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}` }}>
                          <Typography variant="caption" color="text.primary" sx={{ fontStyle: 'italic' }}>{currentCobranca.cliente.observacoes}</Typography>
                        </Box>
                      </Box>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', mb: 1, display: 'block' }}>Canais de Contato</Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {(currentCobranca.cliente as any)?.contatos?.length > 0 ? (
                      (currentCobranca.cliente as any).contatos.map((contato: any) => (
                        <Chip
                          key={contato.id}
                          size="small"
                          icon={contato.tipo === 'email' ? <Email /> : (contato.is_whatsapp ? <WhatsApp /> : <Phone />)}
                          label={`${formatarPhone(contato.numero) || contato.email}${contato.is_default ? ' (Padrão)' : ''}`}
                          variant="outlined"
                          color={contato.is_whatsapp ? 'success' : 'default'}
                          sx={{ mb: 0.5, fontSize: '0.7rem' }}
                        />
                      ))
                    ) : (
                      <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>Nenhum contato cadastrado.</Typography>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Faturas Selecionadas */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Receipt fontSize="small" color="primary" /> Faturas nesta Cobrança
              </Typography>
              {!currentCobranca.nova_fatura_id && (currentCobranca as any)?.faturas?.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  startIcon={<Handshake />}
                  onClick={() => setRenegotiateOpen(true)}
                  sx={{ borderRadius: 2, fontWeight: 700 }}
                >
                  Renegociar Dívida
                </Button>
              )}
            </Box>
            <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Vencimento</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Dias Atraso</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Valor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(currentCobranca as any)?.faturas?.filter((f: any) => String(f.id) !== String(currentCobranca.nova_fatura_id)).map((inv: Invoice) => (
                    <TableRow key={inv.id}>
                      <TableCell>#{inv.id}</TableCell>
                      <TableCell>{formatarData(inv.data_vencimento)}</TableCell>
                      <TableCell sx={{ color: 'error.main', fontWeight: 700 }}>
                        {getDaysOverdue(inv.data_vencimento)} dias
                      </TableCell>
                      <TableCell align="right">{formatarValor(inv.valor)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: alpha(theme.palette.divider, 0.1) }}>
                    <TableCell colSpan={3} sx={{ fontWeight: 800 }}>Total em Cobrança</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900, color: 'error.main', fontSize: '1rem' }}>
                      {formatarValor(currentCobranca.valor_original)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Fatura Renegociada (Nova Fatura) */}
          {currentCobranca?.nova_fatura && (
            <Grid item xs={12}>
              <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.secondary.main, 0.05), border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={800} color="secondary.main" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Handshake fontSize="small" /> Acordo Realizado (Nova Fatura Consolidada)
                </Typography>
                <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.05) }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Vencimento</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Valor do Acordo</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow sx={{ bgcolor: 'background.paper' }}>
                        <TableCell>#{currentCobranca.nova_fatura.id}</TableCell>
                        <TableCell>{formatarData(currentCobranca.nova_fatura.data_vencimento)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={currentCobranca.nova_fatura.status.toUpperCase()} 
                            size="small" 
                            color={currentCobranca.nova_fatura.status === 'pago' ? 'success' : 'info'}
                            sx={{ fontSize: '0.65rem', fontWeight: 900, height: 18 }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 900, color: 'primary.main' }}>
                          {formatarValor(
                            Number(currentCobranca.nova_fatura.valor || 0) + 
                            Number(currentCobranca.nova_fatura.acrescimo || 0) - 
                            Number(currentCobranca.nova_fatura.desconto || 0)
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Grid>
          )}

          {/* Histórico / Movimentações */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <History fontSize="small" color="primary" /> Histórico de Negociação
              </Typography>
              <Button
                startIcon={<Add />}
                variant="contained"
                size="small"
                onClick={() => setAddingMovement(true)}
                sx={{ borderRadius: 2 }}
              >
                Nova Movimentação
              </Button>
            </Box>

            {/* Modal para Adicionar Movimentação */}
            <Dialog
              open={addingMovement}
              onClose={() => !submitting && setAddingMovement(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                <Typography variant="h6" fontWeight={800} color="primary">Registrar Movimentação</Typography>
              </DialogTitle>
              <DialogContent sx={{ pt: 2 }}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select fullWidth size="small" label="Tipo de Contato"
                      value={tipoContato} onChange={(e) => setTipoContato(e.target.value)}
                    >
                      {TIPO_CONTATO_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {opt.icon}
                            <Typography variant="body2">{opt.label}</Typography>
                          </Stack>
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth size="small" label="Falou com"
                      value={falouCom} onChange={(e) => setFalouCom(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      select fullWidth size="small" label="Situação Após Contato"
                      value={statusPosContato} onChange={(e) => setStatusPosContato(e.target.value)}
                    >
                      {STATUS_COBRANCA_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          <Chip size="small" label={opt.label} color={opt.color as any} sx={{ height: 20, fontSize: '0.65rem' }} />
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth size="small" label="Valor Proposta (R$)" type="number"
                      value={valorProposta} onChange={(e) => setValorProposta(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth size="small" label="Data de Promessa" type="date"
                      InputLabelProps={{ shrink: true }}
                      value={dataPromessa} onChange={(e) => setDataPromessa(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth multiline rows={3} label="O que foi conversado?"
                      placeholder="Descreva aqui os detalhes do contato..."
                      value={descricao} onChange={(e) => setDescricao(e.target.value)}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ p: 2, bgcolor: alpha(theme.palette.divider, 0.05) }}>
                <Button onClick={() => setAddingMovement(false)} disabled={submitting}>Cancelar</Button>
                <Button
                  variant="contained"
                  onClick={handleCreateMovement}
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
                >
                  Confirmar Registro
                </Button>
              </DialogActions>
            </Dialog>

            <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Data/Hora</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Agente/Meio</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Negociação</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Proposta/Promessa</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingHistorico ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center"><CircularProgress size={24} sx={{ my: 3 }} /></TableCell>
                    </TableRow>
                  ) : historico.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary', fontStyle: 'italic' }}>
                        Nenhuma movimentação registrada.
                      </TableCell>
                    </TableRow>
                  ) : (
                    historico.map((h) => (
                      <TableRow key={h.id} hover>
                        <TableCell sx={{ fontSize: '0.75rem', verticalAlign: 'top' }}>
                          <Typography variant="caption" display="block" fontWeight={700}>
                            {formatarData(h.createdAt)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(h.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          <Typography variant="caption" display="block" fontWeight={700}>{h.usuario?.nome}</Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {TIPO_CONTATO_OPTIONS.find(o => o.value === h.tipo_contato)?.icon}
                            <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>{h.tipo_contato}</Typography>
                          </Stack>
                          {h.falou_com && (
                            <Typography variant="caption" color="primary.main" display="block">Falo com: {h.falou_com}</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top', maxWidth: 300 }}>
                          <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.2 }}>{h.descricao}</Typography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          {h.valor_proposta && (
                            <Typography variant="caption" display="block" color="success.main" fontWeight={800}>
                              {formatarValor(h.valor_proposta)}
                            </Typography>
                          )}
                          {h.data_promessa && (
                            <Typography variant="caption" display="block" color="warning.main" fontWeight={700}>
                              Promessa: {formatarData(h.data_promessa)}
                            </Typography>
                          )}
                          {!h.valor_proposta && !h.data_promessa && <Typography variant="caption" color="text.disabled">-</Typography>}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          <Chip
                            size="small"
                            label={STATUS_COBRANCA_OPTIONS.find(o => o.value === h.status_pos_contato)?.label}
                            color={STATUS_COBRANCA_OPTIONS.find(o => o.value === h.status_pos_contato)?.color as any}
                            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 800 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: alpha(theme.palette.divider, 0.2) }}>
        <Button onClick={onClose} color="inherit">Fechar</Button>
      </DialogActions>

      {/* Modal de Renegociação */}
      {currentCobranca && (
        <RenegociarCobrancaModal
          open={renegotiateOpen}
          onClose={() => setRenegotiateOpen(false)}
          cobrancaId={currentCobranca.id}
          invoiceIds={(currentCobranca as any).faturas?.filter((f: any) => String(f.id) !== String(currentCobranca.nova_fatura_id)).map((f: any) => f.id) || []}
          onSuccess={() => {
            setRenegotiateOpen(false);
            fetchCobrancaAtualizada();
            if (onSuccess) onSuccess();
          }}
        />
      )}
    </Dialog>
  );
};

export default GerenciarCobrancaModal;
